/**
 * billing-error-copy.ts — user-facing copy for in-app billing error states.
 *
 * in-app-checkout Phase 3 Session 3.3.
 *
 * HARD COPY RULE (apps/web/CLAUDE.md, research §11 #11): all billing error copy
 * is %-only on usage surfaces, no `$`, no "Anthropic", no internal COGS nouns.
 * Inline payment errors (Stripe decline messages) are an exception — they live
 * in their own keyed section below and never surface COGS math.
 *
 * Design principle: these strings are for the END-USER, not the developer. They
 * must be calm, actionable, and never blame Arcanum infrastructure. The underlying
 * error code travels separately (Sentry) for debugging.
 */

/**
 * Generic billing flow error messages (non-card-specific).
 *
 * Keyed by a short semantic identifier so callers never embed copy inline;
 * always import from here so copy remains consistent across surfaces.
 */
export const BILLING_ERROR_COPY = {
  /**
   * Server returned an error when creating the subscription (e.g. Stripe
   * validation failure, invalid tier, user already subscribed).
   */
  SUBSCRIPTION_CREATE_FAILED:
    'Could not start your subscription. Please try again in a moment or contact support.',

  /**
   * The payment was declined by the issuing bank. Not a Stripe error — the
   * card number/expiry/CVC passed Stripe validation but the bank said no.
   */
  PAYMENT_DECLINED:
    'Your payment was declined. Please check your card details and try again, or use a different payment method.',

  /**
   * 3DS / SCA authentication required and the user did not complete the
   * browser popup (e.g. closed the modal, or the timeout expired).
   */
  AUTH_REQUIRED:
    'Additional authentication is required by your bank. Please complete the security challenge and try again.',

  /**
   * 3DS / SCA popup failed or was cancelled. Typically displayed after a
   * `confirmPayment` call resolves with `error.type === 'authentication_error'`.
   */
  AUTH_FAILED:
    'Card authentication failed. Please try again or contact your bank.',

  /**
   * Stripe.js failed to load (`loadStripe` returned null or threw). Usually
   * a network issue or a restrictive browser extension.
   */
  STRIPE_UNAVAILABLE:
    'Secure payment form could not load. Please check your connection and refresh the page.',

  /**
   * `confirmPayment` returned an unexpected error code not otherwise covered.
   * The Stripe error message (safe to display) is appended at the call site.
   */
  CONFIRM_PAYMENT_FAILED: 'Payment could not be completed. Please try again.',

  /**
   * The server returned a 402 / PAYMENT_REQUIRED before the checkout even
   * started (e.g. calling subscription-create while already locked — unlikely
   * but guard-worthy).
   */
  ALREADY_LOCKED:
    'Your account is currently locked. Please contact support to resolve this.',

  /**
   * Generic fallback for any ApiError we have no specific copy for.
   */
  UNEXPECTED_ERROR:
    'Something went wrong. Please refresh the page and try again, or contact support.',
} as const;

export type BillingErrorKey = keyof typeof BILLING_ERROR_COPY;
